import type { AxiosError } from "axios";

/**
 * ADR-TRIAL-card-gate (Phase 5) — мост между axios-перехватчиком (вне React) и
 * React-слоем card-gate. При попытке создать/опубликовать первое боевое trial-событие
 * без карты бэкенд возвращает HTTP 402 с кодом {@link TRIAL_CARD_REQUIRED_CODE}
 * (конверт ADR-002). Перехватчик `http-client.ts` вызывает {@link cardGateController.requestCard}
 * и, дождавшись привязки карты, ПРОЗРАЧНО повторяет исходный запрос. Модель — вариант i:
 * одна верификация карты разблокирует все 3 бесплатных события.
 */

/** Доменный код блокировки публикации без карты (BE events-core, 402). */
export const TRIAL_CARD_REQUIRED_CODE = "TRIAL_CARD_REQUIRED";
/** Доменный код «эта карта уже жгла trial на другом аккаунте» (BE trial.service, 409). */
export const TRIAL_CARD_ALREADY_USED_CODE = "TRIAL_CARD_ALREADY_USED";

/**
 * Достаёт доменный код ошибки из тела ответа ADR-002:
 * `{ success:false, error:{ code, message }, … }`. Приоритет — вложенный `error.code`
 * (канон ADR-002); плоский `code` оставлен как fallback на случай иных форм ответа.
 */
export function extractResponseErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const body = data as { error?: unknown; code?: unknown };
  if (body.error && typeof body.error === "object") {
    const nested = (body.error as { code?: unknown }).code;
    if (typeof nested === "string") return nested;
  }
  if (typeof body.code === "string") return body.code;
  return undefined;
}

/**
 * Универсальное извлечение доменного кода из ошибки: и из сырого AxiosError
 * (`response.data`), и из {@link ApiError} после `HttpClient.handleError`
 * (сырой конверт лежит в `details.responseBody`). `ApiError.code` для доменных
 * ошибок = `HTTP_<status>` (handleError читает плоский `body.code`, которого в
 * ADR-002 нет), поэтому приоритет отдаём телу ответа, а прямой `code` берём только
 * если он НЕ generic-заглушка.
 */
export function extractApiErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const e = err as {
    code?: unknown;
    response?: { data?: unknown };
    details?: { responseBody?: unknown };
  };
  const fromDetails = extractResponseErrorCode(e.details?.responseBody);
  if (fromDetails) return fromDetails;
  const fromResponse = extractResponseErrorCode(e.response?.data);
  if (fromResponse) return fromResponse;
  if (
    typeof e.code === "string" &&
    !e.code.startsWith("HTTP_") &&
    e.code !== "UNKNOWN_ERROR" &&
    e.code !== "NETWORK_ERROR"
  ) {
    return e.code;
  }
  return undefined;
}

/**
 * Подробности отказа из конверта ADR-002 (`error.details`) — спутник
 * {@link extractApiErrorCode} с тем же приоритетом источников: сырой конверт
 * после `HttpClient.handleError` лежит в `details.responseBody`, у голого
 * AxiosError — в `response.data`. Своё поле `details` у `ApiError` — это
 * ОБЁРТКА (`{ status, responseBody }`), а не `error.details` конверта; их легко
 * спутать, поэтому имена здесь разведены явно.
 *
 * Прежде жила приватно в `events/event-delete-error-codes.ts`; вынесена, когда
 * подробности понадобились второму отказу (AD-576, причина отказа в отметке).
 */
export function extractApiErrorDetails(err: unknown): unknown {
  if (!err || typeof err !== "object") return undefined;
  const e = err as {
    response?: { data?: unknown };
    details?: { responseBody?: unknown };
  };
  for (const body of [e.details?.responseBody, e.response?.data]) {
    if (!body || typeof body !== "object") continue;
    const envelope = body as { error?: unknown };
    if (envelope.error && typeof envelope.error === "object") {
      const nested = (envelope.error as { details?: unknown }).details;
      if (nested && typeof nested === "object") return nested;
    }
  }
  return undefined;
}

/** true, если это именно 402 TRIAL_CARD_REQUIRED — сигнал открыть card-gate. */
export function isTrialCardRequiredError(error: AxiosError): boolean {
  return (
    error.response?.status === 402 &&
    extractResponseErrorCode(error.response?.data) === TRIAL_CARD_REQUIRED_CODE
  );
}

type GateListener = (open: boolean) => void;

/**
 * Синглтон-контроллер card-gate. Single-flight: несколько одновременных 402
 * (напр. параллельные publish) ждут ОДИН и тот же экран; после привязки карты все
 * повторяются. Модалку рендерит `CardGateProvider`, подписанный через {@link subscribe}.
 */
class CardGateController {
  private pending: Promise<boolean> | null = null;
  private resolver: ((activated: boolean) => void) | null = null;
  private readonly listeners = new Set<GateListener>();

  /**
   * Запросить привязку карты. Возвращает промис: `true` — карта привязана (можно
   * повторять запрос), `false` — пользователь отменил. Повторный вызов при уже
   * открытом экране возвращает тот же промис (single-flight).
   */
  requestCard(): Promise<boolean> {
    if (this.pending) return this.pending;
    this.pending = new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
    this.notify(true);
    return this.pending;
  }

  /** Завершить card-gate: `true` — карта привязана, `false` — отмена. Вызывает провайдер. */
  complete(activated: boolean): void {
    const resolve = this.resolver;
    this.resolver = null;
    this.pending = null;
    this.notify(false);
    resolve?.(activated);
  }

  /** Открыт ли сейчас card-gate (для идемпотентности провайдера). */
  isOpen(): boolean {
    return this.pending !== null;
  }

  /** Подписка провайдера на открытие/закрытие. Возвращает отписку. */
  subscribe(listener: GateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(open: boolean): void {
    for (const listener of this.listeners) listener(open);
  }
}

export const cardGateController = new CardGateController();
